import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { BASE_REFERRAL_FORMS, TRIAGE_FORM_KEYS } from './referralStatus';

/**
 * Provider-side reads of a record's form instances (TDI-574).
 *
 * The Firestore rule for formInstances withholds every strengths and needs
 * instance (an id starting with `hfp`) until the family has shared the
 * profile with the clinical team: `hfpProfileShared` on the record, kept by
 * the server (TDI-483). A collection read is refused unless the rule holds
 * for every document it could return, so listing the whole subcollection is
 * denied the moment the profile is not shared, and the clinician saw no
 * forms at all, on every record.
 *
 * So: when the record says the profile is shared, listen to the whole
 * subcollection as before. Otherwise listen to the non-HFP instances one
 * document at a time, which the rule allows, taking their ids from the
 * record's formProgress mirror (TDI-154, maintained by every save) and
 * falling back to every referral and history form key for a record without
 * the mirror. The strengths and needs answers never reach the client until
 * the family's yes.
 */

export const ROLE_SUFFIXES = ['_guardian', '_school_user', ''];

export function isHfpInstanceId(instanceId) {
  return /^hfp/i.test(String(instanceId || ''));
}

/**
 * The instance ids a provider may read on this record without the family's
 * yes: every non-HFP id the record's progress mirror lists, or, without a
 * mirror, every referral and history form key with each role suffix.
 */
export function nonHfpInstanceIds(record) {
  const mirrored = Object.keys((record && record.formProgress) || {}).filter((id) => !isHfpInstanceId(id));
  if (mirrored.length > 0) return mirrored.sort();
  const keys = [...BASE_REFERRAL_FORMS, ...TRIAGE_FORM_KEYS];
  return keys.flatMap((key) => ROLE_SUFFIXES.map((suffix) => `${key}${suffix}`));
}

/**
 * Whether the record lets a provider list its whole formInstances
 * subcollection: the flag the rule reads, and nothing else.
 */
export function profileSharedForRules(record) {
  return Boolean(record && record.hfpProfileShared === true);
}

/**
 * A snapshot-shaped view over per-document results, so folds written for a
 * QuerySnapshot (`forEach`, `docs`, `size`) keep working.
 */
function querySnapshotLike(docsById) {
  const docs = Object.values(docsById);
  return { docs, size: docs.length, empty: docs.length === 0, forEach: (fn) => docs.forEach(fn) };
}

/**
 * Listen to the form instances a provider may read on one record.
 *
 * @param {object} args
 * @param {string} args.orgId
 * @param {string} args.patientRecordId
 * @param {object|null} args.record - the patientRecords doc data, when known
 * @param {(snapshot: {docs: object[], forEach: Function, size: number, empty: boolean}) => void} onNext
 * @param {(err: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export function subscribeProviderFormInstances({ orgId, patientRecordId, record }, onNext, onError) {
  if (!orgId || !patientRecordId) return () => {};
  const base = ['rms_organisations', orgId, 'patientRecords', patientRecordId, 'formInstances'];
  if (profileSharedForRules(record)) {
    return onSnapshot(collection(db, ...base), onNext, onError || (() => {}));
  }
  const ids = nonHfpInstanceIds(record);
  const docsById = {};
  const unsubscribes = ids.map((id) =>
    onSnapshot(
      doc(db, ...base, id),
      (snap) => {
        if (snap && typeof snap.exists === 'function' && snap.exists()) docsById[id] = snap;
        else delete docsById[id];
        onNext(querySnapshotLike(docsById));
      },
      onError || (() => {}),
    ),
  );
  // Nothing to listen to still reports an empty, loaded set once.
  if (ids.length === 0) onNext(querySnapshotLike({}));
  return () => unsubscribes.forEach((u) => { try { u(); } catch (_) { /* already gone */ } });
}

/**
 * The common fold: the snapshot as `{ [instanceId]: data }`.
 */
export function instancesById(snapshot) {
  const out = {};
  (snapshot && snapshot.docs ? snapshot.docs : []).forEach((d) => {
    out[d.id] = typeof d.data === 'function' ? d.data() : d.data;
  });
  return out;
}
